import { Mutex } from 'async-mutex';
import Keycloak from 'keycloak-js';

export interface CurrentUser {
  sub: string;
  username: string;
  roles: string[];
}

type AuthTransport = 'bearer' | 'cookie';
type AuthExpiredHandler = () => void | Promise<void>;
type AuthLogoutResult = 'keycloak' | 'server' | 'local';

export interface KeycloakLogoutUrlInput {
  restUrl: string;
  postLogoutRedirectUri: string;
}

let keycloakInstance: Keycloak | undefined;
let currentUser: CurrentUser | undefined;
let authTransport: AuthTransport = 'bearer';
let authExpiredHandler: AuthExpiredHandler | undefined;

export class AuthSessionExpiredError extends Error {
  constructor() {
    super('Authentication session expired');
    this.name = 'AuthSessionExpiredError';
  }
}

export function setKeycloakInstance(kc: Keycloak): void {
  keycloakInstance = kc;
}

export function getKeycloakInstance(): Keycloak | undefined {
  return keycloakInstance;
}

export function setAuthTransport(transport: AuthTransport): void {
  authTransport = transport;
}

export function getAuthTransport(): AuthTransport {
  return authTransport;
}

export function setCurrentUser(user: CurrentUser): void {
  currentUser = user;
}

export function clearAuthState(): void {
  keycloakInstance = undefined;
  currentUser = undefined;
  authTransport = 'bearer';
}

export function setAuthExpiredHandler(
  handler: AuthExpiredHandler | undefined,
): void {
  authExpiredHandler = handler;
}

export async function handleAuthSessionExpired(): Promise<void> {
  clearAuthState();
  await authExpiredHandler?.();
}

const tokenMutex = new Mutex();

export async function getKeycloakToken(): Promise<string | undefined> {
  if (!keycloakInstance) {
    return;
  }

  if (!keycloakInstance.isTokenExpired()) {
    return keycloakInstance.token;
  }

  return tokenMutex.runExclusive(async () => {
    if (!keycloakInstance) {
      return;
    }

    // Need the double check pattern because another thread might have already refreshed the token
    if (keycloakInstance.isTokenExpired()) {
      try {
        await keycloakInstance.updateToken(5); // refresh if expiring in <5s
        console.info('Token refreshed');
      } catch (err) {
        console.error('Failed to refresh token', err);
        return;
      }
    }

    return keycloakInstance.token;
  });
}

// The choice to use "| undefined" instead of "| null" is that "xxx?.roles" is of type "| undefined"
export function getKeycloakRoles(): string[] | undefined {
  return currentUser?.roles;
}

export function getKeycloakSubject(): string | undefined {
  return currentUser?.sub;
}

export function getKeycloakName(): string | undefined {
  return currentUser?.username;
}

export function getKeycloakUsername(): string | undefined {
  return currentUser?.username;
}

export async function fetchCurrentUser(restUrl: string): Promise<CurrentUser> {
  const response = await fetchWithKeycloakToken(
    `${restUrl.replace(/\/$/, '')}/user/current`,
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch current user: ${response.status}`);
  }

  const body = (await response.json()) as Record<string, unknown>;
  setCurrentUser(toCurrentUser(body));
  return currentUser as CurrentUser;
}

// The server serialises this payload as PascalCase in some environments and
// camelCase in others, so accept either rather than silently ending up with an
// undefined role list (which reads as "no permissions" and hides every route).
function toCurrentUser(body: Record<string, unknown>): CurrentUser {
  const pick = <T>(...keys: string[]): T | undefined =>
    keys.map((key) => body[key]).find((value) => value !== undefined) as
      | T
      | undefined;

  return {
    sub: pick<string>('sub', 'Sub') ?? '',
    username: pick<string>('username', 'Username') ?? '',
    roles: pick<string[]>('roles', 'Roles') ?? [],
  };
}

export async function fetchWithKeycloakToken(
  url: string | URL | globalThis.Request,
  options?: RequestInit,
) {
  const headers = new Headers(options?.headers);

  if (authTransport === 'cookie') {
    headers.delete('Authorization');
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers,
    });
  }

  const token = await getKeycloakToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(url, {
    ...options,
    headers,
  });
}

export async function logoutAuthenticatedUser(
  restUrl: string,
): Promise<AuthLogoutResult> {
  if (authTransport === 'cookie') {
    await fetch(`${restUrl.replace(/\/$/, '')}/auth/session/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    clearAuthState();
    return 'server';
  }

  if (keycloakInstance) {
    await keycloakInstance.logout();
    clearAuthState();
    return 'keycloak';
  }

  clearAuthState();
  return 'local';
}
